import { useToast } from '@ouster/webviz-react-components';

import React, { useCallback, useRef } from 'react';

type ToastStatus = 'info' | 'warning' | 'success' | 'error' | 'loading';
type ToastProps = {
  title: string;
  description: React.ReactNode;
  status: ToastStatus;
  id?: string;
  isStay?: boolean;
  closeOnOutsideClick?: boolean;
};

/**
 *
 */
function useChakraHook() {
  const toast = useToast();
  const toastIdRef = useRef<string | undefined>(undefined);

  const closeToast = useCallback(
    (toastId?: string) => {
      const resolvedToastId = toastId ?? toastIdRef.current;

      if (!resolvedToastId) {
        return;
      }

      toast.close(resolvedToastId);

      if (toastIdRef.current === resolvedToastId) {
        toastIdRef.current = undefined;
      }
    },
    [toast]
  );

  /* 
    Wrapper the showToast in useCallback so it won't cause unnecessary re-render
  */
  const showToast = useCallback(
    ({
      title,
      description,
      status,
      id,
      isStay = false,
      closeOnOutsideClick = false,
    }: ToastProps) => {
      const toastOptions = {
        title,
        description,
        status,
        duration: status === 'error' || isStay ? null : 5000,
        isClosable: true,
        position: 'top' as const,
      };

      if (id && toast.isActive(id)) {
        toast.update(id, toastOptions);
        toastIdRef.current = id;
      } else {
        const createdToastId = toast({
          ...(id ? { id } : {}),
          ...toastOptions,
        });

        toastIdRef.current = (id ?? createdToastId)?.toString();
      }

      if (closeOnOutsideClick) {
        const handleClickOutside = () => {
          if (!toastIdRef.current) return;

          if (toastIdRef.current) {
            toast.closeAll();
            toastIdRef.current = undefined;
            document.removeEventListener('mousedown', handleClickOutside);
          }
        };

        document.addEventListener('mousedown', handleClickOutside);
      }
    },
    [toast]
  );

  return { closeToast, showToast };
}

export default useChakraHook;
